// Turns an API error (axios) into something a form can actually show the user.
//
// FastAPI speaks two error shapes:
//   * HTTPException → { detail: "some string" }
//   * Pydantic 422  → { detail: [ { loc: ["body", "end_time"], msg, type }, … ] }
// The 422 array is the one that previously rendered as "[object Object]".
//
// We map the backend's snake_case field names back to the wildfire form's
// camelCase field names so callers can highlight the offending inputs, and we
// build a human-readable summary for an alert banner.

export interface ParsedFieldError {
  field: string;
  msg: string;
}

export interface ParsedApiError {
  /** Human-readable summary suitable for an alert banner. */
  message: string;
  /** Per-field errors mapped to form (camelCase) field names. */
  fieldErrors: ParsedFieldError[];
}

// Backend (snake_case) → wildfire form (camelCase). Inverse of the payload
// map built in WildfireWizard.onSubmit.
const BACKEND_TO_FORM_FIELD: Record<string, string> = {
  start_time: 'startTime',
  end_time: 'endTime',
  area_size_hectares: 'areaSize',
  people_involved: 'peopleInvolved',
  man_hours: 'manHours',
  houses_damaged_count: 'housesDamagedCount',
  houses_estimated_damage: 'housesEstimatedDamage',
  livestock_injured: 'livestockInjured',
  livestock_estimated_damage: 'livestockEstimatedDamage',
  fire_dept_ref: 'fireDeptRef',
  police_ref: 'policeRef',
  internal_notes: 'internalNotes',
  external_notes: 'externalNotes',
  possible_cause: 'possibleCause',
  is_active: 'isActive',
  is_public: 'isPublic',
  type: 'fireType',
  suburb: 'suburb',
  address: 'address',
  injuries: 'injuries',
};

// Friendly labels for the summary banner. Falls back to the field name.
const FIELD_LABEL: Record<string, string> = {
  startTime: 'Start time',
  endTime: 'End time',
  areaSize: 'Area size',
  peopleInvolved: 'People involved',
  manHours: 'Man hours',
  housesDamagedCount: 'Houses damaged',
  housesEstimatedDamage: 'Estimated house damage',
  livestockInjured: 'Livestock injured',
  livestockEstimatedDamage: 'Estimated livestock damage',
  fireDeptRef: 'Fire Dept reference',
  policeRef: 'Police reference',
  internalNotes: 'Internal notes',
  externalNotes: 'External notes',
  possibleCause: 'Possible cause',
  fireType: 'Fire type',
  suburb: 'Suburb',
  address: 'Address',
  injuries: 'Injuries',
};

export function fieldLabel(field: string): string {
  return FIELD_LABEL[field] ?? field;
}

// Pydantic v2 prefixes ValueError messages with "Value error, " — strip it.
function cleanMsg(msg: string): string {
  return String(msg ?? '').replace(/^Value error,\s*/i, '').trim();
}

interface RawValidationItem {
  loc?: unknown[];
  msg?: string;
}

export function parseApiError(err: unknown, fallback = 'Something went wrong'): ParsedApiError {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const detail = (err as any)?.response?.data?.detail;

  // HTTPException — a plain string.
  if (typeof detail === 'string' && detail.trim()) {
    return { message: detail, fieldErrors: [] };
  }

  // 422 — array of { loc, msg }.
  if (Array.isArray(detail)) {
    const fieldErrors: ParsedFieldError[] = [];
    const rootMsgs: string[] = [];

    for (const raw of detail as RawValidationItem[]) {
      const msg = cleanMsg(raw?.msg ?? 'Invalid value');
      const loc = Array.isArray(raw?.loc) ? raw.loc : [];
      // Find the last loc segment that maps to a known backend field.
      let backendField: string | undefined;
      for (let i = loc.length - 1; i >= 0; i--) {
        const seg = loc[i];
        if (typeof seg === 'string' && seg !== 'body' && seg in BACKEND_TO_FORM_FIELD) {
          backendField = seg;
          break;
        }
      }
      const formField = backendField ? BACKEND_TO_FORM_FIELD[backendField] : undefined;
      if (formField) {
        fieldErrors.push({ field: formField, msg });
      } else {
        // No mapped form field — keep the loc path so the user can still tell
        // WHICH thing is wrong. Without this a nested error (e.g. a blank
        // name on the second contact) reads as a bare "String should have at
        // least 1 character" with nothing to attach it to.
        const path = loc
          .filter((seg) => seg !== 'body')
          .map((seg) => (typeof seg === 'number' ? `#${seg + 1}` : String(seg)))
          .join(' → ');
        rootMsgs.push(path ? `${path}: ${msg}` : msg);
      }
    }

    const lines = [
      ...fieldErrors.map((fe) => `${fieldLabel(fe.field)}: ${fe.msg}`),
      ...rootMsgs,
    ];
    const message =
      lines.length > 0
        ? lines.join('\n')
        : 'Please correct the highlighted fields and try again.';
    return { message, fieldErrors };
  }

  // Anything else (network failure, unexpected shape).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const message = (err as any)?.message || fallback;
  return { message, fieldErrors: [] };
}
